<template>
  <div class="prospective-data-annotations">
    <!-- Loading Overlay - show when main image is loading -->
    <div
      v-if="loadingImageSet || (!mainImageLoaded && imageUrl)"
      class="loading-overlay"
    >
      <div class="loading-content">
        <font-awesome-icon
          :icon="['fas', 'sync']"
          spin
          size="3x"
          class="mb-3"
        />
        <p class="loading-text">
          {{ loadingImageSet ? "Loading Data..." : "Loading Image..." }}
        </p>
      </div>
    </div>

    <!-- Image Control Panel -->
    <ImageControlPanel
      :current-image-index="currentImageIndex"
      :total-images="totalImages"
      @previous="handlePreviousImage"
      @next="handleNextImage"
      @close="handleClose"
    />

    <div
      class="annotations-container"
      :class="{ loading: !mainImageLoaded && imageUrl }"
    >
      <!-- Image Section -->
      <div class="image-section">
        <div class="image-wrapper">
          <div v-if="imageUrl" class="image-container">
            <img
              ref="annotatedImage"
              :src="imageUrl"
              alt="Selected image"
              class="main-image"
              :style="imageFilterStyle"
              @load="onImageLoad"
            />
            <canvas
              v-if="mainImageLoaded && !isEditMode"
              ref="canvas"
              :width="canvasWidth"
              :height="canvasHeight"
              class="overlay-canvas"
            />
            <ProspectiveAIEditor
              v-if="isEditMode"
              ref="prospectiveAIEditor"
              :image-src="editorImageSrc"
              :submission="editorSubmission"
              :ai-pathologies="editorAiPathologies"
              :project="project"
              :image-filter-style="imageFilterStyle"
              :show-labels="showLabels"
              @save="handleEditorSave"
              @cancel="handleEditorCancel"
            />
          </div>
          <div v-else class="no-image">
            <font-awesome-icon :icon="['fas', 'image']" size="3x" />
            <p>No image selected</p>
          </div>
        </div>

        <!-- Image Set Navigation -->
        <div
          v-if="isImageSet && imageSet.length > 1"
          class="image-set-scroller"
          :key="'scroller_' + (props.image?.submission?._id || 'none')"
        >
          <div v-if="loadingImageSet" class="loading-text">
            <p>Loading other images in the study...</p>
          </div>
          <div v-else-if="imageSet.length > 0" class="thumbnail-container">
            <button
              v-if="
                primaryImage && imageSetSelected !== primaryImage.submission._id
              "
              class="primary-nav-button"
              @click="navigateToPrimary"
              title="Go to primary image"
            >
              <font-awesome-icon :icon="['fas', 'star']" />
            </button>
            <div class="thumbnail-strip">
              <div
                v-for="img in imageSet"
                :key="'set_' + img.submission._id"
                :class="[
                  'thumbnail-link',
                  img.submission._id === imageSetSelected ? 'active' : ''
                ]"
                @click.prevent.stop="selectImageFromSet(img.submission._id)"
                tabindex="-1"
              >
                <div class="thumbnail-wrapper">
                  <img
                    class="thumbnail-img"
                    :src="getImageUrl(img) + '?thumb=Yes'"
                  />
                  <span
                    v-if="img.submission.image_set_indicator === 'Primary'"
                    class="primary-badge"
                  >
                    <font-awesome-icon :icon="['fas', 'star']" />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Details Section -->
      <div class="details-section" :key="imageDetailsKey">
        <!-- Quick Actions -->
        <div class="detail-card">
          <div class="card-header">
            <h5 class="card-title">Quick Actions</h5>
          </div>
          <div class="card-content quick-actions">
            <div class="detail-item">
              <a class="detail-label" @click.prevent="makeFavourite" href="#">
                <font-awesome-icon
                  :icon="['fas', 'heart']"
                  class="icon"
                  :class="submission?.favourite ? 'font-red' : 'font-yellow'"
                />
                <span>{{
                  submission?.favourite
                    ? "Remove from favourites"
                    : "Add to favourites"
                }}</span>
              </a>
            </div>
            <div
              class="detail-item"
              v-if="
                hasReport &&
                image.report.attachment &&
                image.report.attachment.path
              "
            >
              <a class="detail-label" @click.prevent="downloadPdf" href="#">
                <font-awesome-icon :icon="['fas', 'download']" class="icon" />
                <span>Open / Download Report</span>
              </a>
            </div>
            <div class="detail-item" v-if="hasAIDetections">
              <a
                class="detail-label"
                @click.prevent="showAiPathologies = !showAiPathologies"
                href="#"
              >
                <font-awesome-icon
                  :icon="
                    showAiPathologies ? ['fas', 'eye-slash'] : ['fas', 'eye']
                  "
                  class="icon font-yellow"
                />
                <span>{{
                  showAiPathologies
                    ? "Hide Vendor AI Detections"
                    : "Show Vendor AI Detections"
                }}</span>
              </a>
            </div>
            <div class="detail-item" v-if="hasAIDetections">
              <a
                class="detail-label"
                @click.prevent="showLabels = !showLabels"
                href="#"
              >
                <font-awesome-icon
                  :icon="showLabels ? ['fas', 'tags'] : ['fas', 'tag']"
                  class="icon font-yellow"
                />
                <span>{{
                  showLabels ? "Hide Vendor AI Detection Labels" : "Show Vendor AI Detection Labels"
                }}</span>
              </a>
            </div>
            <div class="detail-item" v-if="hasModerationFeedback">
              <a
                class="detail-label"
                @click.prevent="
                  showFeedbackPathologies = !showFeedbackPathologies
                "
                href="#"
              >
                <font-awesome-icon
                  :icon="
                    showFeedbackPathologies
                      ? ['fas', 'eye-slash']
                      : ['fas', 'eye']
                  "
                  class="icon font-yellow"
                />
                <span>{{
                  showFeedbackPathologies
                    ? "Hide Radiologist Audit Findings"
                    : "Show Radiologist Audit Findings"
                }}</span>
              </a>
            </div>
            <div class="detail-item" v-if="hasAIDetections && canModerate">
              <a
                class="detail-label"
                @click.prevent="toggleAuditWindow"
                href="#"
              >
                <font-awesome-icon
                  :icon="auditWindowOpen ? ['fas', 'eye-slash'] : ['fas', 'eye']"
                  class="icon font-yellow"
                />
                <span
                  >{{ auditWindowOpen ? "Close" : "Open" }} Radiologist Audit
                  Window</span
                >
              </a>
            </div>
          </div>
        </div>

        <!-- Image Details Card -->
        <ImageDetailsCard
          v-if="!draggableCards['image-details']?.isDraggable"
          :submission="submission"
          :metadata="metadata"
          :metadata-open="metadataOpen"
          :image-name="imageName"
          :project="project"
          @toggle-draggable="toggleDraggable('image-details')"
          @toggle-metadata="metadataOpen = !metadataOpen"
          mode="parked"
        />

        <!-- Report Card -->
        <ReportCard
          v-if="hasReport && !draggableCards['report']?.isDraggable"
          :image="image"
          :submission="submission"
          @toggle-draggable="toggleDraggable('report')"
          mode="parked"
        />

        <!-- AI Detections Card -->
        <VendorAIDetectionsCard
          v-if="hasAIDetections && !draggableCards['vendor-ai']?.isDraggable"
          :submission="submission"
          :ai-pathologies="vendorAiPathologies"
          :project="project"
          @toggle-draggable="toggleDraggable('vendor-ai')"
          mode="parked"
        />

        <!-- RATIFY Analysis Card -->
        <RatifyAnalysisCard
          v-if="
            hasRatifyAnalysis && !draggableCards['ratify-analysis']?.isDraggable
          "
          :image="image"
          :llm-pathologies="llmPathologies"
          @toggle-draggable="toggleDraggable('ratify-analysis')"
          mode="parked"
        />

        <!-- Moderation/Feedback Card -->
        <ModerationFeedbackCard
          v-if="
            hasModerationFeedback && !draggableCards['moderation']?.isDraggable
          "
          :latest-moderation="latestModeration"
          :ai-feedback-accuracy="aiFeedbackAccuracy"
          :ai-pathologies="moderatedPathologies"
          @toggle-draggable="toggleDraggable('moderation')"
          mode="parked"
        />

        <!-- Image Enhancement Controls -->
        <ImageEnhancementCard
          v-if="!draggableCards['enhancement']?.isDraggable"
          v-model:brightness="brightness"
          v-model:contrast="contrast"
          v-model:saturate="saturate"
          v-model:sepia="sepia"
          v-model:invert="invert"
          @toggle-draggable="toggleDraggable('enhancement')"
          mode="parked"
        />
      </div>
    </div>

    <!-- Draggable Overlays -->
    <div
      v-draggable
      class="slidein draggable-overlay"
      :class="{ open: draggableCards['image-details']?.isDraggable }"
      v-if="draggableCards['image-details']?.isDraggable"
    >
      <ImageDetailsCard
        :submission="submission"
        :metadata="metadata"
        :metadata-open="metadataOpen"
        :image-name="imageName"
        :project="project"
        @toggle-draggable="toggleDraggable('image-details')"
        @toggle-metadata="metadataOpen = !metadataOpen"
        mode="draggable"
      />
    </div>

    <!-- Report Draggable -->
    <div
      v-draggable
      class="slidein draggable-overlay"
      :class="{ open: draggableCards['report']?.isDraggable }"
      v-if="draggableCards['report']?.isDraggable"
    >
      <ReportCard
        :image="image"
        :submission="submission"
        @toggle-draggable="toggleDraggable('report')"
        mode="draggable"
      />
    </div>

    <!-- Vendor AI Draggable -->
    <div
      v-draggable
      class="slidein draggable-overlay"
      :class="{ open: draggableCards['vendor-ai']?.isDraggable }"
      v-if="draggableCards['vendor-ai']?.isDraggable"
    >
      <VendorAIDetectionsCard
        :submission="submission"
        :ai-pathologies="vendorAiPathologies"
        :project="project"
        @toggle-draggable="toggleDraggable('vendor-ai')"
        mode="draggable"
      />
    </div>

    <!-- RATIFY Analysis Draggable -->
    <div
      v-draggable
      class="slidein draggable-overlay"
      :class="{ open: draggableCards['ratify-analysis']?.isDraggable }"
      v-if="draggableCards['ratify-analysis']?.isDraggable"
    >
      <RatifyAnalysisCard
        :image="image"
        :llm-pathologies="llmPathologies"
        @toggle-draggable="toggleDraggable('ratify-analysis')"
        mode="draggable"
      />
    </div>

    <!-- Moderation Draggable -->
    <div
      v-draggable
      class="slidein draggable-overlay"
      :class="{ open: draggableCards['moderation']?.isDraggable }"
      v-if="draggableCards['moderation']?.isDraggable"
    >
      <ModerationFeedbackCard
        :latest-moderation="latestModeration"
        :ai-feedback-accuracy="aiFeedbackAccuracy"
        :ai-pathologies="moderatedPathologies"
        @toggle-draggable="toggleDraggable('moderation')"
        mode="draggable"
      />
    </div>

    <!-- Enhancement Draggable -->
    <div
      v-draggable
      class="slidein draggable-overlay"
      :class="{ open: draggableCards['enhancement']?.isDraggable }"
      v-if="draggableCards['enhancement']?.isDraggable"
    >
      <ImageEnhancementCard
        v-model:brightness="brightness"
        v-model:contrast="contrast"
        v-model:saturate="saturate"
        v-model:sepia="sepia"
        v-model:invert="invert"
        @toggle-draggable="toggleDraggable('enhancement')"
        mode="draggable"
      />
    </div>

    <!-- Vendor AI Audit Draggable -->
    <div
      v-draggable
      class="slidein draggable-overlay"
      :class="{ open: draggableCards['vendor-ai-audit']?.isDraggable }"
      v-if="draggableCards['vendor-ai-audit']?.isDraggable"
    >
      <VendorAIDetectionsAuditCard
        :ai-pathologies="aiPathologies"
        :project="project"
        :available-options="availableOptions"
        :saving-data="savingAudit"
        v-model:shownList="shownList"
        v-model:concordanceList="concordanceList"
        v-model:otherTags="other_tags"
        @save="saveAuditAnalysis"
        @toggle-draggable="toggleDraggable('vendor-ai-audit')"
        mode="draggable"
      />
    </div>

    <!-- AI Detection Editor - Save Confirmation Modal -->
    <div
      v-if="showSaveConfirmation"
      class="editor-confirm-overlay"
      @click.self="cancelSaveConfirmation"
    >
      <div class="editor-confirm-modal">
        <div class="confirm-header">
          <font-awesome-icon :icon="['fas', 'save']" class="header-icon" />
          <h3>Save Radiologist Audit?</h3>
        </div>
        <div class="confirm-body" v-if="pendingEditorChanges">
          <p class="confirm-text">
            Please confirm the following changes will be saved:
          </p>
          <ul class="change-summary">
            <li v-if="pendingEditorChanges.summary.totalAfter >= 0">
              <font-awesome-icon :icon="['fas', 'list']" class="summary-icon" />
              <span
                ><b>{{ pendingEditorChanges.summary.totalAfter }}</b> detection(s)
                will be saved</span
              >
            </li>
            <li v-if="pendingEditorChanges.summary.added > 0">
              <font-awesome-icon :icon="['fas', 'plus']" class="summary-icon" />
              <span
                ><b>{{ pendingEditorChanges.summary.added }}</b> new detection(s)
                added</span
              >
            </li>
            <li v-if="pendingEditorChanges.summary.deleted > 0">
              <font-awesome-icon :icon="['fas', 'trash']" class="summary-icon" />
              <span
                ><b>{{ pendingEditorChanges.summary.deleted }}</b> detection(s)
                removed (marked as discordant)</span
              >
            </li>
          </ul>
          <p class="confirm-note">
            Coordinates will be updated and concordance will be recorded for each
            detection.
          </p>
        </div>
        <div class="confirm-footer">
          <button
            type="button"
            class="confirm-btn cancel"
            :disabled="savingChanges"
            @click.prevent="cancelSaveConfirmation"
          >
            <font-awesome-icon :icon="['fas', 'times']" />
            <span>Keep Editing</span>
          </button>
          <button
            type="button"
            class="confirm-btn save"
            :disabled="savingChanges"
            @click.prevent="confirmSaveChanges"
          >
            <font-awesome-icon
              :icon="['fas', savingChanges ? 'spinner' : 'check']"
              :spin="savingChanges"
            />
            <span>{{ savingChanges ? "Saving..." : "Confirm Save" }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Navigation guard: unsaved AI detection changes -->
    <BModal
      v-model="showNavigationWarning"
      title="Leave this image?"
      ok-title="Leave anyway"
      cancel-title="Stay"
      @ok="confirmNavigation"
      @cancel="cancelNavigation"
    >
      <p>
        You have unsaved edits to this image. If you leave, those edits will be
        lost.
      </p>
    </BModal>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import { useStore } from "vuex";
import { apiRoute } from "@/helpers/api-route";
import { drawLabels } from "@/helpers/canvasLabelRenderer";
import { canLabel } from "@/helpers/project-management/roleCheck";
import { staffSubmissionService } from "@/services/staff.submission.service";
import ImageControlPanel from "@/modules/workspace/components/Shared/ImageControlPanel.vue";
import ImageDetailsCard from "@/modules/workspace/components/Shared/ImageDetailsCard.vue";
import ReportCard from "@/modules/workspace/components/Shared/ReportCard.vue";
import VendorAIDetectionsCard from "@/modules/workspace/components/Shared/VendorAIDetectionsCard.vue";
import RatifyAnalysisCard from "@/modules/workspace/components/Shared/RatifyAnalysisCard.vue";
import ModerationFeedbackCard from "@/modules/workspace/components/Shared/ModerationFeedbackCard.vue";
import ImageEnhancementCard from "@/modules/workspace/components/Shared/ImageEnhancementCard.vue";
import VendorAIDetectionsAuditCard from "@/modules/workspace/components/Shared/VendorAIDetectionsAuditCard.vue";
import ProspectiveAIEditor from "@/modules/workspace/components/DataAnnotations/ProspectiveAIEditor.vue";

const props = defineProps({
  image: {
    type: Object,
    required: true,
  },
  project: {
    type: Object,
    default: null,
  },
  currentImageIndex: {
    type: Number,
    default: 0,
  },
  totalImages: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["closeBottom", "navigatePrevious", "navigateNext"]);

const store = useStore();

// Refs
const annotatedImage = ref(null);
const canvas = ref(null);
const resizeObserver = ref(null);

// State
const mainImageLoaded = ref(false);
const canvasWidth = ref(0);
const canvasHeight = ref(0);
const ratio = ref(1);
const loadingImageSet = ref(false);
const imageSet = ref([]);
const imageSetSelected = ref(null);
const imageDetailsKey = ref(0);

// Draggable cards state
const draggableCards = ref({});

// Enhancement controls
const brightness = ref(100);
const contrast = ref(100);
const saturate = ref(100);
const sepia = ref(0);
const invert = ref(0);

// Expandable sections
const metadataOpen = ref(false);

// Visibility toggles
const showAiPathologies = ref(true);
const showFeedbackPathologies = ref(false);
const showLabels = ref(true);
const shownList = ref({});
const concordanceList = ref({});
const other_tags = ref([]);
const savingAudit = ref(false);

// Edit mode state
const prospectiveAIEditor = ref(null);
const isEditMode = ref(false);
const showSaveConfirmation = ref(false);
const savingChanges = ref(false);
const pendingEditorChanges = ref(null);
// Saved panel collapse state so it can be restored when exiting the editor.
const previousLeftPanelCollapsed = ref(null);
const previousRightPanelCollapsed = ref(null);

// Frozen editor inputs - once editing starts the editor is decoupled from live
// props so that navigation attempts (or external image-prop changes) can't wipe
// unsaved edits. The editor binds to these, not to the live computeds.
const editorImageSrc = ref(null);
const editorSubmission = ref(null);
const editorAiPathologies = ref([]);

// Navigation guard state - any attempt to leave the editor while there are
// unsaved changes is funnelled through a confirmation modal.
const showNavigationWarning = ref(false);
const pendingNavAction = ref(null);
const pendingImageSnapshot = ref(null); // for external image-prop changes
const editModeImageSnapshot = ref(null); // store state to revert to on "stay"
const suppressImageGuard = ref(false); // prevents re-entrancy while reverting
// Hard stop: while the user has just confirmed leaving, the image watcher must
// not re-evaluate the guard (its reads of isEditMode/dirty are ambiguous during
// the editor unmount + image-swap flush).
const isLeaving = ref(false);

// Colors for pathologies
const colours = [
  "#C77D3D",
  "#A4414A",
  "#7D1E5F",
  "#5C0F6B",
  "#3C0A6F",
  "#1E0A6E",
  "#0B0C63",
  "#0A1052",
  "#0C1441",
  "#13192F",
  "#1D1E1E",
  "#2A2213",
  "#3A2709",
  "#4B2C00",
  "#5D3100",
  "#6F3600",
  "#813B00",
  "#933F00",
  "#A54200",
  "#B74400",
];

// Computed properties
const submission = computed(() => {
  const selectedId = imageSetSelected.value;
  const selected = selectedId
    ? imageSet.value.find((i) => i.submission._id === selectedId)
    : props.image;
  return selected?.submission || null;
});

const firstAttachment = computed(() => {
  return submission.value?.attachments?.[0] || null;
});

const imageUrl = computed(() => {
  if (!firstAttachment.value || !firstAttachment.value.path) return null;

  const path = firstAttachment.value.path;
  if (path.indexOf("http") < 0) {
    return `${apiRoute()}${path}`;
  }
  return path;
});

const imageName = computed(() => {
  if (!submission.value) return "Unknown";

  if (submission.value.study_id) {
    return submission.value.study_id;
  } else if (firstAttachment.value) {
    return firstAttachment.value.name;
  }

  return "Unknown";
});

const hasReport = computed(() => {
  return !!props.image?.report;
});

const canModerate = computed(() => {
  return canLabel(store, props.project._id);
});

const hasAIDetections = computed(() => {
  return (
    submission.value?.status === "Completed" ||
    (firstAttachment.value?.response && firstAttachment.value.response._id)
  );
});

const hasRatifyAnalysis = computed(() => {
  return props.image?.reportAnalysis?.status === "Completed";
});

const hasModerationFeedback = computed(() => {
  return props.image?.submission?.attachments?.[0]?.feedback;
});

const metadata = computed(() => {
  if (!submission.value?.metadata || !props.project) return [];

  const projectMetadata = submission.value.metadata.find(
    (m) => m.project === props.project._id,
  );
  return projectMetadata?.values || [];
});

const isImageSet = computed(() => {
  return (
    submission.value?.image_set && imageSet.value && imageSet.value.length >= 1
  );
});

const primaryImage = computed(() => {
  if (imageSet.value && imageSet.value.length > 0) {
    return imageSet.value.find(
      (i) => i.submission.image_set_indicator === "Primary",
    );
  }
  return null;
});

const selectedImage = computed(() =>
  imageSetSelected.value
    ? imageSet.value.find((i) => i.submission._id === imageSetSelected.value)
    : props.image,
);

const response = computed(
  () => selectedImage.value?.submission?.attachments?.[0]?.response || null,
);

// Concordance feedback, keyed by pathology_id.
const feedbackList = computed(() => {
  try {
    return (
      selectedImage.value?.submission?.attachments?.[0]?.feedback?.labels?.[0]
        ?.pathologies || null
    );
  } catch (error) {
    console.error("Error processing feedback:", error);
    return null;
  }
});

// Wrap a raw `labels[0].pathologies` array in the display shape used by the
// cards/overlays, matching concordance feedback and assigning overlay colours.
const buildPathologyList = (rawPathologies) => {
  if (!rawPathologies) return [];
  return rawPathologies.map((pathology, index) => {
    let feedback = true;
    let hasFeedback = false;
    const feedbackItem = feedbackList.value?.find(
      (p) => p.pathology_id === pathology._id,
    );
    if (feedbackItem) {
      feedback = feedbackItem.concordance;
      hasFeedback = true;
    }
    pathology.colour = colours[index % colours.length];
    return { ai: pathology, feedback, hasFeedback, project: null };
  });
};

// Current response labels = the radiologist-audited detections once an audit
// has been performed (otherwise the original vendor AI output). Used by the
// audit window, editor, and moderation card.
const aiPathologies = computed(() =>
  buildPathologyList(response.value?.labels?.[0]?.pathologies),
);

// Original vendor AI detections. When a radiologist audit replaces the labels,
// the untouched AI output is snapshotted into `response.previous`; the earliest
// snapshot (by replaced_at) holds it, in a labels array of the same shape as
// the parent. Before any audit `previous` is empty and the current labels are
// the vendor AI output.
const vendorAiPathologies = computed(() => {
  const previous = response.value?.previous;
  if (previous && previous.length > 0) {
    const earliest = previous.reduce((a, b) =>
      new Date(a.replaced_at) <= new Date(b.replaced_at) ? a : b,
    );
    return buildPathologyList(earliest?.labels?.[0]?.pathologies);
  }
  return buildPathologyList(response.value?.labels?.[0]?.pathologies);
});

// Radiologist audit findings = the current (audited) labels, but only once an
// audit has actually replaced the vendor AI output (a `previous` snapshot
// exists). No audit yet -> no findings.
const moderatedPathologies = computed(() =>
  response.value?.previous?.length > 0 ? aiPathologies.value : [],
);

// Diagnosis-only detections (no drawable geometry) require the advanced editor;
// otherwise the simple audit window is enough.
const useAdvancedAudit = computed(() =>
  aiPathologies.value.some((p) => p.ai.isDiagnosis === false),
);

const auditWindowOpen = computed(() =>
  useAdvancedAudit.value
    ? isEditMode.value
    : !!draggableCards.value["vendor-ai-audit"]?.isDraggable,
);

const toggleAuditWindow = () => {
  if (useAdvancedAudit.value) {
    toggleEditMode();
  } else {
    toggleDraggable("vendor-ai-audit");
  }
};

const llmPathologies = computed(() => {
  if (
    props.image?.reportAnalysis?.aiPathologies &&
    props.image.reportAnalysis.aiPathologies.length > 0
  ) {
    return props.image.reportAnalysis.aiPathologies;
  }
  return [];
});

const latestModeration = computed(() => {
  const feedback = firstAttachment.value?.feedback;
  if (!feedback) return null;

  return feedback;
});

const aiFeedbackAccuracy = computed(() => {
  if (!props.image?.reportAnalysis?.feedback) return 0;

  const feedback = props.image.reportAnalysis.feedback;
  const total = feedback.TP + feedback.FP + feedback.FN + feedback.TN;
  if (total === 0) return 0;

  return (feedback.TP + feedback.TN) / total;
});

const imageFilterStyle = computed(() => {
  return `filter: brightness(${brightness.value}%) contrast(${contrast.value}%) saturate(${saturate.value}%) sepia(${sepia.value}%) invert(${invert.value}%);`;
});

const projectId = computed(() => {
  return props.project?._id;
});

const currentImageIndex = computed(() => {
  return props.currentImageIndex;
});

const availableOptions = computed(() => {
  if (
    props.project &&
    props.project.pathologies &&
    props.project.pathologies.length > 0
  ) {
    return props.project.pathologies
      .filter((p) => p.primary)
      .map((p) => p.label);
  } else {
    return [];
  }
});

// Methods
const recalculateCanvasDimensions = () => {
  if (!annotatedImage.value) return;

  const img = annotatedImage.value;
  const naturalWidth = img.naturalWidth;
  const naturalHeight = img.naturalHeight;
  const containerWidth = img.clientWidth;
  const containerHeight = img.clientHeight;

  // Calculate the actual displayed size of the image with object-fit: contain
  const imageAspect = naturalWidth / naturalHeight;
  const containerAspect = containerWidth / containerHeight;

  let displayedWidth, displayedHeight;

  if (imageAspect > containerAspect) {
    // Image is wider - width is the limiting factor
    displayedWidth = containerWidth;
    displayedHeight = containerWidth / imageAspect;
  } else {
    // Image is taller - height is the limiting factor
    displayedHeight = containerHeight;
    displayedWidth = containerHeight * imageAspect;
  }

  const newRatio = displayedWidth / naturalWidth;

  // Only update if dimensions actually changed to avoid unnecessary redraws
  if (
    displayedWidth !== canvasWidth.value ||
    displayedHeight !== canvasHeight.value ||
    newRatio !== ratio.value
  ) {
    canvasWidth.value = Math.round(displayedWidth);
    canvasHeight.value = Math.round(displayedHeight);
    ratio.value = newRatio;

    // Redraw annotations with new dimensions
    nextTick(() => {
      drawImageAndBoxes();
    });
  }
};

const setupResizeObserver = () => {
  if (!annotatedImage.value) return;

  // Clean up existing observer if any
  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
  }

  // Create new ResizeObserver to monitor image size changes
  resizeObserver.value = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.target === annotatedImage.value) {
        recalculateCanvasDimensions();
      }
    }
  });

  // Start observing the image element
  resizeObserver.value.observe(annotatedImage.value);
};

const onImageLoad = () => {
  mainImageLoaded.value = true;

  // Force rerender of details section when image loads
  imageDetailsKey.value += 1;

  if (!annotatedImage.value) return;

  // Set initial dimensions using the recalculation method
  recalculateCanvasDimensions();

  // Set up ResizeObserver to monitor future size changes
  setupResizeObserver();

  // Draw initial annotations
  nextTick(() => {
    drawImageAndBoxes();
  });
};

// Draw a set of pathologies onto the overlay canvas. Cardio/Thoracic are
// always skipped (no drawable geometry). In audit mode, discordant detections
// (feedback === false) are hidden. Markers are drawn first, then labels are
// batched through the shared label renderer (off-ROI chips + collision
// avoidance), gated on the "show labels" toggle.
const drawPathologyList = (ctx, pathologies, options = {}) => {
  const { auditMode = false } = options;
  const labels = [];
  const markers = [];
  pathologies.forEach((pathology, index) => {
    if (
      pathology.ai.predicted_diagnosis === "Cardio" ||
      pathology.ai.predicted_diagnosis === "Thoracic"
    )
      return;
    if (shownList.value[pathology.ai._id] === false) return;
    if (auditMode && pathology.feedback === false) return;

    const colour = pathology.ai.colour || colours[index % colours.length];
    const name = pathology.project?.label || pathology.ai.predicted_diagnosis;

    let res = null;
    if (
      pathology.ai.freehandpoints &&
      pathology.ai.freehandpoints.length > 1
    ) {
      res = drawFreehandAnnotation(ctx, pathology.ai, colour, name);
    } else if (
      pathology.ai.bounding_box &&
      pathology.ai.bounding_box.x !== undefined
    ) {
      res = drawPathologyBox(ctx, pathology.ai, colour, name);
    }
    if (res) {
      markers.push(res.marker);
      if (res.label) {
        // Attach the finding's marker so the label renderer can draw the leader
        // to the marker's nearest edge (not a fixed top anchor).
        res.label.marker = res.marker;
        labels.push(res.label);
      }
    }
  });

  if (showLabels.value) {
    drawLabels(ctx, labels, canvasWidth.value, canvasHeight.value, markers);
  }
};

const drawImageAndBoxes = () => {
  if (!canvas.value || !mainImageLoaded.value) return;

  const ctx = canvas.value.getContext("2d");
  ctx.clearRect(0, 0, canvas.value.width, canvas.value.height);

  if (vendorAiPathologies.value.length === 0) return;

  // Only vendor AI detections are drawn on the read-only overlay, controlled
  // by the "Show Vendor AI Detections" / "Show Radiologist Audit Findings"
  // toggles. Moderator-added detections (no AI confidence) are managed via
  // the Audit Window and Editor, not the overlay.
  if (showAiPathologies.value) {
    drawPathologyList(ctx, vendorAiPathologies.value);
  } else if (showFeedbackPathologies.value) {
    // Audit findings: moderated detections (AI the radiologist confirmed +
    // moderator-added) excluding discordant ones.
    drawPathologyList(ctx, moderatedPathologies.value, { auditMode: true });
  }
};

// Draw a bounding-box marker. Returns { marker, label }: marker is the
// display-space rect (for label collision avoidance); label is a spec for the
// shared label renderer, or null if there's no box / no name.
const drawPathologyBox = (ctx, pathology, colour, name) => {
  if (!pathology.bounding_box || pathology.bounding_box.x === undefined)
    return null;

  const box = pathology.bounding_box;

  // Draw bounding box
  ctx.lineWidth = 2;
  ctx.strokeStyle = colour;
  ctx.strokeRect(
    box.x * ratio.value,
    box.y * ratio.value,
    box.w * ratio.value,
    box.h * ratio.value,
  );

  const marker = {
    x: box.x * ratio.value,
    y: box.y * ratio.value,
    w: box.w * ratio.value,
    h: box.h * ratio.value,
  };

  if (!name) return { marker, label: null };

  return {
    marker,
    label: {
      text: name,
      colour,
      anchorX: (box.x + box.w / 2) * ratio.value,
      anchorY: box.y * ratio.value,
    },
  };
};

// Draw a freehand polygon marker. Returns { marker, label } — marker is the
// display-space bounding rect (for collision avoidance); label includes the
// confidence % in its text. Returns null if there are no points.
const drawFreehandAnnotation = (ctx, annotation, colour, name) => {
  if (!annotation.freehandpoints || annotation.freehandpoints.length < 2)
    return null;

  // Draw filled polygon
  ctx.lineWidth = 2;
  ctx.strokeStyle = colour;
  ctx.fillStyle = colour + "33"; // Add transparency

  ctx.beginPath();
  ctx.moveTo(
    annotation.freehandpoints[0].x * ratio.value,
    annotation.freehandpoints[0].y * ratio.value,
  );

  for (let i = 1; i < annotation.freehandpoints.length; i++) {
    const point = annotation.freehandpoints[i];
    ctx.lineTo(point.x * ratio.value, point.y * ratio.value);
  }

  ctx.closePath();
  ctx.stroke();
  ctx.fill();

  // Display-space bounding rect for label collision avoidance.
  const xs = annotation.freehandpoints.map((p) => p.x * ratio.value);
  const ys = annotation.freehandpoints.map((p) => p.y * ratio.value);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const marker = {
    x: minX,
    y: minY,
    w: Math.max(...xs) - minX,
    h: Math.max(...ys) - minY,
  };

  let displayText = name;
  if (annotation.confidence) {
    displayText = `${name} (${Math.round(annotation.confidence * 100)}%)`;
  }

  // Anchor the label above the polygon's top edge, centred on its centroid.
  const cx =
    (annotation.freehandpoints.reduce((s, p) => s + p.x, 0) /
      annotation.freehandpoints.length) *
    ratio.value;

  return {
    marker,
    label: { text: displayText, colour, anchorX: cx, anchorY: minY },
  };
};

const getImageUrl = (img) => {
  if (!img?.submission?.attachments?.[0]?.path) return "";

  const path = img.submission.attachments[0].path;
  if (path.indexOf("http") < 0) {
    return `${apiRoute()}${path}`;
  }
  return path;
};

const navigateToPrimary = () => {
  if (!primaryImage.value) return;
  const primaryId = primaryImage.value.submission._id;
  if (primaryId === imageSetSelected.value) return;

  guardNavigation(() => {
    imageSetSelected.value = primaryId;

    // Scroll to primary thumbnail
    nextTick(() => {
      setTimeout(() => {
        const scrollerContainer = document.querySelector(".thumbnail-strip");
        const primaryThumbnail = document.querySelector(
          ".thumbnail-link.primary",
        );

        if (scrollerContainer && primaryThumbnail) {
          const containerHeight = scrollerContainer.clientHeight;
          const thumbnailTop = primaryThumbnail.offsetTop;
          const thumbnailHeight = primaryThumbnail.clientHeight;
          const scrollTo =
            thumbnailTop - containerHeight / 2 + thumbnailHeight / 2;

          scrollerContainer.scrollTo({
            top: Math.max(0, scrollTo),
            behavior: "smooth",
          });
        }
      }, 50);
    });
  });
};

const downloadPdf = () => {
  if (!props.image?.report?.attachment) return;

  const attachment = props.image.report.attachment;
  if (attachment.path.indexOf("http") === -1) {
    window.open(
      `${apiRoute()}/image/report/${props.image.report._id}`,
      "_blank",
    );
  } else {
    window.open(attachment.path, "_blank");
  }
};

const handleClose = () => {
  guardNavigation(() => emit("closeBottom"));
};

const handlePreviousImage = () => {
  guardNavigation(() => emit("navigatePrevious"));
};

const handleNextImage = () => {
  guardNavigation(() => emit("navigateNext"));
};

// Select another image within the same study (thumbnail click).
const selectImageFromSet = (id) => {
  if (id === imageSetSelected.value) return;
  guardNavigation(() => {
    imageSetSelected.value = id;
  });
};

const makeFavourite = async () => {
  if (!submission.value) return;

  try {
    await staffSubmissionService.toggleFavourite(submission.value._id);
    submission.value.favourite = !submission.value.favourite;
  } catch (error) {
    console.error("Error toggling favourite status:", error);
  }
};

const toggleDraggable = (cardId) => {
  const currentState = draggableCards.value[cardId]?.isDraggable || false;
  draggableCards.value[cardId] = {
    isDraggable: !currentState,
    position: null,
  };
};

const saveAuditAnalysis = async () => {
  if (
    !submission.value ||
    !submission.value.attachments ||
    submission.value.attachments.length === 0
  )
    return;

  savingAudit.value = true;
  try {
    const attachment = submission.value.attachments[0];
    const responseId = attachment.response._id;
    const reportConclusion = props.image?.report?.conclusion || "";

    // Convert concordance list to expected format
    const pathologies = [];
    for (const prop in concordanceList.value) {
      // Basic check to include only relevant IDs
      if (prop !== "other" && !prop.startsWith("new_")) {
        pathologies.push({
          id: prop,
          value: concordanceList.value[prop],
        });
      }
    }

    const payload = {
      id: responseId,
      pathologies: pathologies,
      diagnosis: reportConclusion ? [reportConclusion] : [], // Use report conclusion as diagnosis baseline
      other: [...other_tags.value],
      submission: submission.value._id,
    };

    await staffSubmissionService.feedback(
      responseId,
      pathologies,
      payload.other,
      payload.diagnosis,
    );

    // Close the audit modal on success
    if (draggableCards.value["vendor-ai-audit"]) {
      draggableCards.value["vendor-ai-audit"].isDraggable = false;
    }

    const _newSubmission = await staffSubmissionService.get(
      submission.value._id,
      props.project?._id,
    );
    await store.dispatch("workspacePanel/setUpdatedImage", {
      id: submission.value._id,
      value: _newSubmission,
      action: "feedback",
    });
    store.dispatch("pmsMetrics/recompute", {
      project: props.project?._id,
      force: true,
    });
  } catch (error) {
    console.error("Error saving audit analysis:", error);
  } finally {
    savingAudit.value = false;
  }
};

// ===== Prospective AI Detection Editor =====
// Whether the editor currently holds unsaved edits.
const editorIsDirty = () => {
  return isEditMode.value && prospectiveAIEditor.value?.isDirty() === true;
};

// Freeze the editor's inputs so navigation attempts and external image-prop
// changes cannot recompute them (which would silently discard edits).
const enterEditMode = () => {
  editorImageSrc.value = imageUrl.value;
  editorSubmission.value = submission.value;
  editorAiPathologies.value = aiPathologies.value;
  editModeImageSnapshot.value = {
    image: props.image,
    imageType: store.state.workspacePanel.selectedImageType,
    imageIndex: props.currentImageIndex,
    totalImages: props.totalImages,
  };
  // Collapse both sidebars for more editor space, remembering the prior state
  // so it can be restored on exit.
  previousLeftPanelCollapsed.value =
    store.state.workspacePanel.leftPanelCollapsed;
  previousRightPanelCollapsed.value =
    store.state.workspacePanel.rightPanelCollapsed;
  store.dispatch("workspacePanel/setLeftPanelCollapsed", true);
  store.dispatch("workspacePanel/setRightPanelCollapsed", true);
  isEditMode.value = true;
};

const exitEditMode = () => {
  isEditMode.value = false;
  editorImageSrc.value = null;
  editorSubmission.value = null;
  editorAiPathologies.value = [];
  // Restore the sidebar collapse state captured on enter (if any).
  if (previousLeftPanelCollapsed.value !== null) {
    store.dispatch(
      "workspacePanel/setLeftPanelCollapsed",
      previousLeftPanelCollapsed.value,
    );
    previousLeftPanelCollapsed.value = null;
  }
  if (previousRightPanelCollapsed.value !== null) {
    store.dispatch(
      "workspacePanel/setRightPanelCollapsed",
      previousRightPanelCollapsed.value,
    );
    previousRightPanelCollapsed.value = null;
  }
};

const toggleEditMode = () => {
  if (isEditMode.value) {
    // Exiting via the quick action: confirm if there are unsaved edits.
    if (editorIsDirty()) {
      pendingNavAction.value = () => exitEditMode();
      showNavigationWarning.value = true;
    } else {
      exitEditMode();
    }
  } else {
    enterEditMode();
  }
};

const handleEditorSave = (changes) => {
  pendingEditorChanges.value = changes;
  showSaveConfirmation.value = true;
};

const handleEditorCancel = () => {
  // The editor already confirmed (or had no changes) before emitting cancel.
  exitEditMode();
  pendingEditorChanges.value = null;
  showSaveConfirmation.value = false;
};

// ===== Navigation guard =====
// Run a navigation action only if there are no unsaved edits; otherwise ask.
// When there are no unsaved edits the editor is closed first, so its frozen
// inputs can't go stale as the underlying image changes underneath it. When
// there ARE unsaved edits, the confirmed action also closes the editor first,
// which guarantees isEditMode is false during the subsequent navigation so the
// image watcher can't re-trigger the guard.
const guardNavigation = (action) => {
  if (editorIsDirty()) {
    pendingNavAction.value = () => {
      exitEditMode();
      action();
    };
    showNavigationWarning.value = true;
    return;
  }
  if (isEditMode.value) exitEditMode();
  action();
};

const confirmNavigation = () => {
  showNavigationWarning.value = false;
  const action = pendingNavAction.value;
  pendingNavAction.value = null;
  pendingImageSnapshot.value = null;
  if (action) {
    // Hold the guard off until the resulting image change has been processed.
    isLeaving.value = true;
    action();
    // Fallback reset for navigations that don't change props.image (e.g.
    // imageSetSelected). Runs after the watcher flush, so for the dispatch case
    // the accepted-path reset (which runs in that flush) wins.
    nextTick(() => {
      isLeaving.value = false;
    });
  }
};

const cancelNavigation = () => {
  showNavigationWarning.value = false;
  pendingNavAction.value = null;
  pendingImageSnapshot.value = null;
};

const cancelSaveConfirmation = () => {
  showSaveConfirmation.value = false;
  // Keep editor open with pending changes so the user can keep adjusting.
  prospectiveAIEditor.value?.saveDone();
};

// Build the concordance/feedback payload after the refresh.
// - Deleted originals   -> concordance false
// - Surviving originals -> concordance true (includes untouched Cardio/Thoracic)
// - Newly added         -> concordance true (matched from refreshed submission)
const buildEditorFeedbackPathologies = (changes, updatedSubmission) => {
  const originalSet = new Set(changes.originalIds);
  const deletedSet = new Set(changes.deletedIds);
  const pathologies = [];

  changes.originalIds.forEach((id) => {
    pathologies.push({ id, value: !deletedSet.has(id) });
  });

  const refreshedPathologies =
    updatedSubmission?.attachments?.[0]?.response?.labels?.[0]?.pathologies ||
    [];
  refreshedPathologies.forEach((p) => {
    if (p._id && !originalSet.has(p._id)) {
      pathologies.push({ id: p._id, value: true });
    }
  });

  return pathologies;
};

const confirmSaveChanges = async () => {
  if (!pendingEditorChanges.value || !submission.value) return;

  savingChanges.value = true;
  try {
    const changes = pendingEditorChanges.value;
    const editedId = submission.value._id;
    const attachment = submission.value.attachments[0];
    const responseId = attachment.response._id;

    // 1. Update coordinates (includes Cardio/Thoracic passthrough + surviving
    //    edited originals + newly added detections).
    await staffSubmissionService.update(
      responseId,
      [{ pathologies: changes.updatePathologies, reasons: null }],
      [],
    );

    // 2. Refresh submission so newly added pathologies have server-assigned ids.
    const updatedSubmission = await staffSubmissionService.get(
      editedId,
      props.project?._id,
    );

    // 3. Record concordance for every detection.
    const feedbackPathologies = buildEditorFeedbackPathologies(
      changes,
      updatedSubmission,
    );
    await staffSubmissionService.feedback(
      responseId,
      feedbackPathologies,
      other_tags.value,
      [], // diagnosis: left empty per product decision
    );

    // 4. Re-fetch so the displayed data reflects both the new coordinates and
    //    the concordance/feedback that was just recorded. Normalise the shape:
    //    `get` returns a submission, but be defensive in case it is wrapped.
    const refreshed = await staffSubmissionService.get(
      editedId,
      props.project?._id,
    );
    const refreshedSubmission = refreshed?.submission
      ? refreshed.submission
      : refreshed;

    // 5. Reflect the refreshed submission in the local image set so computeds
    //    (aiPathologies, latestModeration, etc.) recompute with fresh data.
    const setIdx = imageSet.value.findIndex(
      (i) => i.submission._id === editedId,
    );
    if (setIdx !== -1) {
      imageSet.value[setIdx] = {
        ...imageSet.value[setIdx],
        submission: refreshedSubmission,
      };
    }

    // 6. Refresh the store's selected image too, so computeds reading from
    //    props.image (e.g. when there is no image set) see the new data. Only
    //    do this when the edited image IS the selected one - otherwise this
    //    would change the selected id and trigger a full study reload.
    if (editedId === props.image?.submission?._id) {
      store.dispatch("workspacePanel/setSelectedImage", {
        image: { ...props.image, submission: refreshedSubmission },
        imageType: store.state.workspacePanel.selectedImageType,
        imageIndex: props.currentImageIndex,
        totalImages: props.totalImages,
      });
    }

    // 7. Reset the audit/visibility maps so stale entries (e.g. temporary
    //    new_ ids from the editor) don't linger.
    concordanceList.value = {};
    shownList.value = {};

    // 8. Push the refreshed image into the store for LeftPanel synchronisation.
    //    Prefer the (now-updated) image set entry so the wrapper stays correct
    //    even when the edited image differs from the originally selected one.
    const updatedImageEntry =
      setIdx !== -1
        ? imageSet.value[setIdx]
        : { ...props.image, submission: refreshedSubmission };
    await store.dispatch("workspacePanel/setUpdatedImage", {
      id: editedId,
      value: updatedImageEntry,
      action: "coordinates_updated",
    });
    store.dispatch("pmsMetrics/recompute", {
      project: props.project?._id,
      force: true,
    });

    // 9. Keep both sidebars collapsed after saving - the user may want to edit
    //    another image in the study. Drop the captured previous state so
    //    exitEditMode() won't restore it.
    previousLeftPanelCollapsed.value = null;
    previousRightPanelCollapsed.value = null;

    // 10. Close editor + modal.
    exitEditMode();
    showSaveConfirmation.value = false;
    pendingEditorChanges.value = null;

    // 11. Force the details section to rerender and the overlay canvas to
    //     redraw with the refreshed pathology data.
    imageDetailsKey.value += 1;
    nextTick(() => {
      drawImageAndBoxes();
    });
  } catch (error) {
    console.error("Error saving AI detection changes:", error);
  } finally {
    savingChanges.value = false;
    prospectiveAIEditor.value?.saveDone();
  }
};

// Watchers
watch(imageUrl, () => {
  mainImageLoaded.value = false;

  // Clean up existing ResizeObserver when image changes
  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
    resizeObserver.value = null;
  }
});

// Redraw the read-only overlay canvas when leaving the AI editor.
watch(isEditMode, (active) => {
  if (!active) {
    nextTick(() => {
      drawImageAndBoxes();
    });
  }
});

watch(imageSetSelected, async () => {
  mainImageLoaded.value = false;

  // Wait for the DOM to update with the new image URL
  await nextTick();

  // Check if the image element has the same src (cached image)
  // If so, we need to manually trigger the load handler since @load won't fire for cached images
  if (annotatedImage.value && annotatedImage.value.complete && annotatedImage.value.naturalHeight !== 0) {
    // Image is already loaded from cache - call onImageLoad manually
    onImageLoad();
  }
  // Otherwise, the @load event will fire naturally
});

watch(
  [showAiPathologies, showLabels, aiPathologies, shownList],
  () => {
    if (showAiPathologies.value) {
      showFeedbackPathologies.value = false;
    }

    // Initialize concordance/shown maps for all pathologies (the Audit Window
    // manages both vendor AI and moderator-added detections).
    if (aiPathologies.value && aiPathologies.value.length > 0) {
      aiPathologies.value.forEach((p) => {
        if (concordanceList.value[p.ai._id] === undefined) {
          concordanceList.value[p.ai._id] = p.feedback;
        }
        if (shownList.value[p.ai._id] === undefined) {
          shownList.value[p.ai._id] = true;
        }
      });
    }

    nextTick(() => {
      drawImageAndBoxes();
    });
  },
  { deep: true, immediate: true },
);

watch(showFeedbackPathologies, () => {
  if (showFeedbackPathologies.value) {
    showAiPathologies.value = false;
  }

  nextTick(() => {
    drawImageAndBoxes();
  });
});

watch(
  latestModeration,
  (newVal) => {
    if (newVal && newVal.other) {
      other_tags.value = [...newVal.other];
    } else {
      other_tags.value = [];
    }
  },
  { immediate: true },
);

// Watch for project selection removed, i.e., Home button clicked
watch(projectId, () => {
  if (!projectId.value) {
    handleClose();
  }
});

// Watch for image changes to reload image set
watch(
  () => props.image?.submission?._id,
  (newId, oldId) => {
    console.log("Image changed:", { newId, oldId });

    // Always clear when ID changes (including undefined -> defined)
    if (newId === oldId) return;

    // If the editor has unsaved edits, an external change (e.g. selecting
    // another case from the left panel) must not silently discard them. Capture
    // the incoming selection, revert the store to the image being edited (the
    // editor is frozen so this does not affect it), and ask the user.
    if (
      isEditMode.value &&
      !suppressImageGuard.value &&
      !isLeaving.value &&
      editorIsDirty()
    ) {
      // Capture the incoming selection NOW (props.image is already the new
      // image). It is closed over by the pending action so later clearing of
      // the ref can't null it out before the action runs.
      const incomingSnapshot = {
        image: props.image,
        imageType: store.state.workspacePanel.selectedImageType,
        imageIndex: props.currentImageIndex,
        totalImages: props.totalImages,
      };
      pendingImageSnapshot.value = incomingSnapshot;
      pendingNavAction.value = () => {
        exitEditMode();
        store.dispatch("workspacePanel/setSelectedImage", incomingSnapshot);
      };

      // Revert to the image the user was editing.
      suppressImageGuard.value = true;
      store.dispatch(
        "workspacePanel/setSelectedImage",
        editModeImageSnapshot.value,
      );
      nextTick(() => {
        suppressImageGuard.value = false;
      });

      showNavigationWarning.value = true;
      return;
    }

    // Editor open without unsaved edits: close it so its frozen inputs don't go
    // stale. (Skipped while suppressing, i.e. mid-revert of a guarded change.)
    if (isEditMode.value && !suppressImageGuard.value) {
      exitEditMode();
    }

    // The leaving transition is complete once an image change is accepted.
    isLeaving.value = false;

    console.log("Clearing imageSet for new study");
    // Clear previous image set immediately
    imageSet.value = [];
    imageSetSelected.value = null;
    mainImageLoaded.value = false;

    // Load new image set if available
    if (props.image?.submission?.image_set && props.project?._id) {
      console.log("Loading image set for study:", newId);
      nextTick(() => {
        loadImageSet(props.image);
      });
    } else {
      console.log("No image set for this study");
    }
  },
  { immediate: true },
);

// Methods for loading image sets
const loadImageSet = async (imageData = null) => {
  const targetImage = imageData || props.image;

  if (!targetImage?.submission?.image_set || !props.project?._id) {
    console.log("Cannot load image set - missing data:", {
      hasImageSet: !!targetImage?.submission?.image_set,
      hasProjectId: !!props.project?._id,
    });
    return;
  }

  try {
    loadingImageSet.value = true;

    // Determine the grouping identifier (study_id or patient.identifier)
    const targetStudyId = targetImage.submission.study_id;
    const targetPatientId = targetImage.submission.patient?.identifier;

    console.log(
      "Fetching image set for submission:",
      targetImage.submission._id,
      "with study_id:",
      targetStudyId,
      "patient:",
      targetPatientId,
      "in project:",
      props.project._id,
    );

    const images = await staffSubmissionService.getForSet(
      targetImage.submission._id,
      props.project._id,
    );

    console.log(`Received ${images?.length || 0} images from API`);

    if (images && images.length > 0) {
      // Filter to only include images that belong to the same study/patient
      const filteredImages = images.filter((img) => {
        // Match by study_id if available
        if (targetStudyId && img.submission.study_id === targetStudyId) {
          return true;
        }
        // Otherwise match by patient identifier
        if (
          targetPatientId &&
          img.submission.patient?.identifier === targetPatientId
        ) {
          return true;
        }
        return false;
      });

      console.log(
        `Filtered to ${filteredImages.length} images matching study_id: ${targetStudyId} or patient: ${targetPatientId}`,
      );
      console.log(
        "Filtered images:",
        filteredImages.map((img) => ({
          id: img.submission._id,
          indicator: img.submission.image_set_indicator,
          study_id: img.submission.study_id,
          patient: img.submission.patient?.identifier,
        })),
      );

      if (filteredImages.length > 0) {
        imageSet.value = filteredImages;
        imageSetSelected.value = targetImage.submission._id;
      } else {
        console.log("No images matched the study_id/patient filter");
        imageSet.value = [];
        imageSetSelected.value = null;
      }
    } else {
      console.log("No images returned from getForSet");
      imageSet.value = [];
      imageSetSelected.value = null;
    }
  } catch (error) {
    console.error("Error loading image set:", error);
    imageSet.value = [];
    imageSetSelected.value = null;
  } finally {
    loadingImageSet.value = false;
  }
};

// Lifecycle
onMounted(async () => {
  // Image set loading is now handled by the watcher with immediate: true
  console.log("ProspectiveDataAnnotations mounted");

  // canModerate() depends on the auth user (primary role source) and, as a
  // fallback, the project members list. On a hard refresh the Vuex stores are
  // empty, so rehydrate both before the permission-gated UI reads them.
  if (!store.state.auth?.user) {
    await store.dispatch("auth/initAuth");
  }
  if (props.project?._id) {
    store.dispatch("usersSubmission/getMembers", props.project._id);
  }
});

onUnmounted(() => {
  // Clean up ResizeObserver
  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
    resizeObserver.value = null;
  }
});
</script>

<style scoped>
.prospective-data-annotations {
  position: relative;
  min-height: calc(100vh - var(--header-height));
  background-color: var(--color-bg-secondary);
  padding: 1rem 0.75rem 1rem 1rem;
}

.close-button-container {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
}

.annotations-container {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 1rem;
  height: calc(100vh - var(--header-height) - 2rem);
}

/* Image Section */
.image-section {
  display: flex;
  gap: 1rem;
  background-color: var(--color-bg-secondary);
  border: var(--border-width-card) solid var(--color-border-secondary);
  border-radius: var(--border-radius-standard);
  padding: 1rem;
  overflow: hidden;
  height: 100%;
}

.image-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 0;
  min-width: 0;
  width: 100%;
  height: 100%;
}

.image-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-image {
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.overlay-canvas {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.no-image {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  gap: 1rem;
}

.no-image p {
  margin: 0;
  font-size: 1.1rem;
}

/* Image Set Scroller */
.image-set-scroller {
  width: 104px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: var(--color-bg-primary);
  border-radius: var(--border-radius-standard);
  padding: 0.5rem 0.5rem 0.5rem 0.5rem;
  position: relative;
}

.loading-text {
  text-align: center;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  padding: 1rem;
}

.thumbnail-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
}

.thumbnail-strip {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  padding: 0 2px;
  overscroll-behavior: contain;
}

.primary-nav-button {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: var(--color-accent-primary);
  color: var(--color-bg-primary);
  border: none;
  border-radius: var(--border-radius-small);
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.5rem;
  flex-shrink: 0;
}

.primary-nav-button:hover {
  background-color: var(--color-accent-hover);
}

.thumbnail-link {
  display: block;
  position: relative;
  border: 2px solid transparent;
  border-radius: var(--border-radius-small);
  overflow: hidden;
  transition: border-color 0.2s ease;
  cursor: pointer;
  width: 100%;
  flex-shrink: 0;
  outline: none;
  scroll-margin: 0;
  user-select: none;
}

.thumbnail-link:hover {
  border-color: var(--color-border-hover);
}

.thumbnail-link:focus {
  outline: none;
}

.thumbnail-link.active {
  border-color: var(--color-accent-primary);
}

.thumbnail-link.primary {
  box-shadow: 0 0 0 2px var(--color-accent-primary);
}

.thumbnail-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  min-height: 0;
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.primary-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  background-color: var(--color-accent-primary);
  color: var(--color-bg-primary);
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .annotations-container {
    grid-template-columns: 1fr 350px;
  }
}

.me-2 {
  margin-right: 0.5rem;
}

.quick-actions .icon {
  margin-right: 0.5rem;
}

.quick-actions .detail-label {
  text-decoration: none;
}

/* ===== AI Detection Editor - Save Confirmation Modal ===== */
.editor-confirm-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
}

.editor-confirm-modal {
  background-color: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: var(--border-radius-standard);
  box-shadow: var(--shadow-standard, 0 10px 30px rgba(0, 0, 0, 0.3));
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.confirm-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--color-border-primary);
  background-color: var(--color-bg-secondary);
}

.confirm-header .header-icon {
  font-size: 1.1rem;
  color: var(--color-accent-primary, #ffc107);
}

.confirm-header h3 {
  margin: 0;
  font-size: 1.05rem;
  color: var(--color-text-primary);
}

.confirm-body {
  padding: 1.1rem 1.25rem;
}

.confirm-text {
  margin: 0 0 0.75rem;
  font-size: 0.9rem;
  color: var(--color-text-primary);
}

.change-summary {
  list-style: none;
  margin: 0 0 0.75rem;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.change-summary li {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.85rem;
  color: var(--color-text-primary);
  padding: 0.5rem 0.6rem;
  background-color: var(--color-bg-secondary);
  border-radius: var(--border-radius-small);
}

.change-summary .summary-icon {
  color: var(--color-accent-primary, #ffc107);
  width: 16px;
  text-align: center;
}

.confirm-note {
  margin: 0;
  font-size: 0.78rem;
  color: var(--color-text-secondary);
  font-style: italic;
  line-height: 1.4;
}

.confirm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.9rem 1.25rem;
  border-top: 1px solid var(--color-border-primary);
  background-color: var(--color-bg-secondary);
}

.confirm-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-small);
  border: 1px solid var(--color-border-primary);
  background-color: var(--color-bg-primary);
  color: var(--color-text-primary);
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.15s ease;
}

.confirm-btn:hover:not(:disabled) {
  border-color: var(--color-border-hover);
}

.confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.confirm-btn.save {
  background-color: var(--color-success, #28a745);
  color: #fff;
  border-color: var(--color-success, #28a745);
}

.confirm-btn.save:hover:not(:disabled) {
  filter: brightness(1.08);
}

.confirm-btn.cancel:hover:not(:disabled) {
  background-color: var(--color-danger, #dc3545);
  color: #fff;
  border-color: var(--color-danger, #dc3545);
}
</style>
